import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import SystemNotification from '@/app/components/feedback/SystemNotification';
import { isTicketsMockEnabled } from '@/app/consts/featureFlags';
import { getCatalog, getTicket, listSlaDeadlines, listTickets, openTicket, replyToTicket } from './_requests';
import {
  type Ticket,
  type TicketDepartment,
  TicketListTab,
  type TicketOpenInput,
  type TicketSlaDeadline,
} from './_models';
import { ticketsInTab } from './_utils';

export const ticketKeys = {
  all: ['tickets'] as const,
  list: () => [...ticketKeys.all, 'list'] as const,
  detail: (id: string) => [...ticketKeys.all, 'detail', id] as const,
  catalog: () => [...ticketKeys.all, 'catalog'] as const,
  slaDeadlines: () => [...ticketKeys.all, 'sla-deadlines'] as const,
};

const QUERY_OPTIONS = {
  retry: false,
  throwOnError: false,
  meta: { suppressGlobalError: true },
} as const;

const TICKET_LIST_QUERY_OPTIONS = {
  queryKey: ticketKeys.list(),
  queryFn: async () => {
    const response = await listTickets();
    return response.data;
  },
  ...QUERY_OPTIONS,
};

export function useTickets() {
  return useQuery(TICKET_LIST_QUERY_OPTIONS);
}

export function useTicketCatalog(): UseQueryResult<TicketDepartment[], Error> {
  return useQuery({
    queryKey: ticketKeys.catalog(),
    queryFn: async () => {
      const response = await getCatalog();
      return response.data;
    },
    enabled: isTicketsMockEnabled,
    ...QUERY_OPTIONS,
  });
}

export function useTicketSlaDeadlines(): UseQueryResult<TicketSlaDeadline[], Error> {
  return useQuery({
    queryKey: ticketKeys.slaDeadlines(),
    queryFn: async () => {
      const response = await listSlaDeadlines();
      return response.data;
    },
    enabled: isTicketsMockEnabled,
    ...QUERY_OPTIONS,
  });
}

export function useWaitingTicketsCount(): number {
  const ticketsQuery = useQuery({ ...TICKET_LIST_QUERY_OPTIONS, enabled: isTicketsMockEnabled });
  const tickets = ticketsQuery.data ?? [];
  return ticketsInTab(tickets, TicketListTab.WAITING).length;
}

export function useTicket(id: string) {
  return useQuery({
    queryKey: ticketKeys.detail(id),
    queryFn: async () => {
      const response = await getTicket(id);
      return response.data;
    },
    ...QUERY_OPTIONS,
  });
}

export function useReplyToTicket(id: string) {
  const queryClient = useQueryClient();
  return useMutation<Ticket, Error, string>({
    mutationFn: async (text) => {
      const response = await replyToTicket(id, text);
      return response.data;
    },
    onSuccess: (ticket) => {
      const detailKey = ticketKeys.detail(id);
      const listKey = ticketKeys.list();
      queryClient.setQueryData(detailKey, ticket);
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      SystemNotification.error(message);
    },
  });
}

export function useOpenTicket() {
  const queryClient = useQueryClient();
  return useMutation<Ticket, Error, TicketOpenInput>({
    mutationFn: async (input) => {
      const response = await openTicket(input);
      return response.data;
    },
    onSuccess: (ticket) => {
      const detailKey = ticketKeys.detail(ticket.id);
      const listKey = ticketKeys.list();
      queryClient.setQueryData(detailKey, ticket);
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      SystemNotification.error(message);
    },
  });
}
